import React, { useEffect, useState, useCallback } from 'react';
import { message, Table, Spin } from 'antd';
import { Wrapper } from './style';
import { MakeApiCall } from '../../../../apis';
import { ConvertParams } from '../../../../config';

const formatValue = (value, key, currencySign) => {
  if (value === null || value === undefined) return '-';

  // Percentage fields
  const percentageFields = [
    'unit_session_percent',
    'real_acos',
    'refunds_percent', // Make sure it's 'refunds_percent' not 'refunds_pect'
    'sellable_returns'
  ];

  // Numeric (integer) fields (clicks, units, sessions, etc)
  const integerFields = [
    'units',
    'units.organic',
    'units.sp_units',
    'units.sb_units',
    'units.sd_units',
    'sessions',
    'active_subscriptions',
    'advertising_clicks',
    'advertising_clicks.sp_click',
    'advertising_clicks.sb_click',
    'advertising_clicks.sd_click'
  ];

  if (key.endsWith('refunds_percent')) {
    const num = Number(value);
    if (isNaN(num)) return value;
    // If value is above 1, treat as already percent, just add % sign; else multiply
    if (Math.abs(num) > 1) {
      return `${num.toFixed(2)}%`;
    } else {
      return `${(num * 100).toFixed(2)}%`;
    }
  }

  // Margin needs 2 decimal places as percent
  if (key.endsWith('margin')) return `${parseFloat(value).toFixed(2)}%`;

  // ROI must be shown as currency
  if (key.endsWith('roi')) {
    const num = Number(value);
    if (isNaN(num)) return value;
    const sign = num < 0 ? '-' : '';
    return `${sign}${currencySign}${Math.abs(num).toFixed(2)}`;
  }

  // Percentage fields (with 0 decimals except margin above)
  const isPercentage = percentageFields.some(field => key.endsWith(field));
  if (isPercentage) return `${(parseFloat(value) * 100).toFixed(0)}%`;

  // Integer fields
  const isInteger = integerFields.some(field => key.endsWith(field));
  if (isInteger) return parseInt(value).toLocaleString();

  // Currency formatting (place negative before symbol)
  if (typeof value === 'number') {
    const sign = value < 0 ? '-' : '';
    return `${sign}${currencySign}${Math.abs(value).toFixed(2)}`;
  }

  return value;
};

const flattenData = (obj, prefix = '') => {
  return Object.entries(obj).reduce((acc, [key, val]) => {
    const path = prefix ? `${prefix}.${key}` : key;
    if (val && typeof val === 'object' && !Array.isArray(val)) {
      Object.assign(acc, flattenData(val, path));
    } else {
      acc[path] = val;
    }
    return acc;
  }, {});
};

const monthOrderMap = {
  Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
  Jul: 7, Aug: 8, Sep: 9, Oct: 10, Nov: 11, Dec: 12
};

const sortMonthYear = (arr) => {
  return arr.sort((a, b) => {
    const [monthA, yearA] = a.split(" ");
    const [monthB, yearB] = b.split(" ");

    const yA = parseInt(yearA, 10);
    const yB = parseInt(yearB, 10);

    if (yA !== yB) return yA - yB;
    return monthOrderMap[monthA] - monthOrderMap[monthB];
  });
};

/** Custom Hook */
const useProfitAndLossData = (filters, token, selectedCurrencySign) => {
  const [columns, setColumns] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const buildTable = useCallback((data) => {
    if (!data?.length) {
      setColumns([]);
      setRows([]);
      return;
    }

    // const dateKeys = [...new Set(data.map(entry => entry.report_date))].sort();
    const dateKeys = sortMonthYear([...new Set(data.map(entry => entry.report_date))]).reverse();


    const dynamicColumns = [
      {
        title: 'Parameter',
        dataIndex: 'parameter',
        key: 'parameter',
        fixed: 'left',
        width: 270,
        render: (text) => <span style={{ fontWeight: 'bold' }}>{text}</span>,
      },
      ...dateKeys.map(date => ({
        title: date,
        dataIndex: date,
        key: date,
        align: 'right',
        width: 140,
        render: (value, record) => formatValue(value, record.key, selectedCurrencySign),
      })),
      {
        title: 'Total',
        dataIndex: 'total',
        key: 'total',
        align: 'right',
        className: 'fw-bold bdr',
        width: 150,
        render: (value, record) => (
            <div className='fw-bold'>
              {formatValue(value, record.key, selectedCurrencySign)}
            </div>
        ),
      },
    ];

    const dateMap = data.reduce((acc, item) => {
      acc[item.report_date] = flattenData(item);
      return acc;
    }, {});

    const metricMap = {};
    data.forEach(entry => {
      const flat = flattenData(entry);
      Object.keys(flat).forEach(key => {
        const parts = key.split('.');
        const category = parts[0];
        const metric = parts.length > 1 ? parts.slice(1).join('.') : null;

        // Skip totals
        if (metric === 'total') return;

        // Skip the 'real_acos' metric entirely so it won't be displayed
        const metricName = metric ? metric : category;
        if (metricName === 'real_acos') return;

        if (!metricMap[category]) {
          metricMap[category] = { hasChildren: false, metrics: [] };
        }

        if (metric) {
          metricMap[category].hasChildren = true;
          if (!metricMap[category].metrics.some(m => m.key === key)) {
            metricMap[category].metrics.push({
              key,
              label: metric.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
            });
          }
        } else {
          metricMap[category].metrics.push({
            key: category,
            label: category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
          });
        }
      });
    });

    const isPercentageKey = (key) =>
        key.endsWith('refunds_percent') ||
        key.endsWith('margin') ||
        key.endsWith('unit_session_percent') ||
        key.endsWith('real_acos') ||
        key.endsWith('sellable_returns');

    const allRows = [];

    Object.entries(metricMap).forEach(([category, { hasChildren, metrics }]) => {
      if (hasChildren) {
        const parentRow = {
          key: category,
          parameter: category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
        };

        parentRow.children = metrics
            .filter(metric => !metric.key.includes('.total'))
            .map(metric => {
              const childRow = { key: metric.key, parameter: metric.label };

              let total = 0;
              let count = 0;

              dateKeys.forEach(date => {
                const value = dateMap[date]?.[metric.key];
                childRow[date] = value;
                if (typeof value === 'number' && !isNaN(value)) {
                  if (isPercentageKey(metric.key)) {
                    total += value;
                    count += 1;
                  } else {
                    total += value;
                  }
                }
              });

              childRow.total = isPercentageKey(metric.key) && count > 0 ? total / count : total;
              return childRow;
            });

        if (parentRow.children.length > 0) {
          const totalKey = `${category}.total`;
          const firstKey = parentRow.children[0].key;
          let total = 0;
          let count = 0;
          let sampleKey = dateMap[dateKeys[0]][totalKey] !== undefined ? totalKey : firstKey;

          dateKeys.forEach(date => {
            const value = dateMap[date]?.[sampleKey];
            parentRow[date] = value;
            if (typeof value === 'number' && !isNaN(value)) {
              if (isPercentageKey(sampleKey)) {
                total += value;
                count += 1;
              } else {
                total += value;
              }
            }
          });

          parentRow.total = isPercentageKey(sampleKey) && count > 0 ? total / count : total;
          allRows.push(parentRow);
        }
      } else {
        const row = { key: metrics[0].key, parameter: metrics[0].label };
        let total = 0;
        let count = 0;
        dateKeys.forEach(date => {
          const value = dateMap[date]?.[metrics[0].key];
          row[date] = value;
          if (typeof value === 'number' && !isNaN(value)) {
            if (isPercentageKey(metrics[0].key)) {
              total += value;
              count += 1;
            } else {
              total += value;
            }
          }
        });
        row.total = isPercentageKey(metrics[0].key) && count > 0 ? total / count : total;
        allRows.push(row);
      }
    });

    setColumns(dynamicColumns);
    setRows(allRows);
  }, [selectedCurrencySign]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const response = await MakeApiCall(
          `dashboard/profit-and-loss/get-summary-data?${ConvertParams(filters)}`,
          'get',
          null,
          true,
          {},
          token
      );

      if (response?.status) {
        buildTable(response.data?.summaryData || []);
      } else {
        message.warning(response?.message);
        setColumns([]);
        setRows([]);
      }

      setLoading(false);
    };

    fetchData();
  }, [filters, token]);

  return { columns, rows, loading };
};

/** Table Component */
const ProfitLossTable = ({ columns, rows }) => {
  return (
      <div className="table-responsive ">
        <Table
            scroll={{ x: 'max-content' }}
            columns={columns}
            dataSource={rows}
            // NOTE: Removed `loading` prop from Ant Table to handle the overlay manually
            rowKey="key"
            pagination={false}
            expandable={{
              defaultExpandAllRows: false,
              rowExpandable: record => record.children?.length > 0,
            }}
            showHeader={rows.length > 0}
            components={{
              table: (props) => (
                  <table
                      className="table align-middle table-row-dashed table-row-gray-300 gy-4 gs-4 gx-4 border-top-d cus-table"
                      {...props}
                  />
              ),
              header: {
                wrapper: (props) => <thead {...props} className="fw-bolder text-dark bg-gray-cus" />,
                cell: (props) => <th {...props} className="px-4 py-4" />,
              },
              body: {
                cell: (props) => <td {...props} className="px-4 py-4" />,
              },
            }}
        />
      </div>
  );
};

/** Main Component */
const PL = ({ periodicity, otherRange, userData, selectedMarketplace, selectedCurrencySign, selectedSku }) => {

  // Initialize filters with props
  const [filters, setFilters] = useState({
    page: 1,
    'per-page': 20,
    marketplace_id: selectedMarketplace?.id,
    report_type: periodicity,
    start_date: otherRange.start_date,
    end_date: otherRange.end_date,
    sku_lists: selectedSku?.join(",")
  });

  // Update filters when props change
  useEffect(() => {
    setFilters(prev => ({
      ...prev,
      marketplace_id: selectedMarketplace?.id,
      report_type: periodicity,
      start_date: otherRange.start_date,
      end_date: otherRange.end_date,
      sku_lists: selectedSku?.join(",")
    }));
  }, [selectedMarketplace, periodicity, otherRange, selectedSku]);

  const { columns, rows, loading } = useProfitAndLossData(filters, userData?.token, selectedCurrencySign);

  const content = (() => {
    if (loading) {
      // 1. Display loader when loading is true, centered with a min height
      return (
          <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '400px', // Adjust this height as needed
                width: '100%',
              }}
          >
            <Spin size="large" tip="Loading data..." />
          </div>
      );
    }

    if (rows?.length > 0) {
      // 2. Display table when rows exist and not loading
      return <ProfitLossTable columns={columns} rows={rows} />;
    }

    // 3. Display SVG/No Data message when loading is false and no rows exist
    return (
        <div className="d-flex flex-column align-items-center justify-content-center py-10" style={{minHeight: '400px'}}>
          <img
              src="/assets/media/no-data.png"
              alt="No data"
              style={{maxWidth: "250px", marginBottom: "20px"}}
          />
          <h5 className="text-muted">No data found</h5>
        </div>
    );
  })();

  return (
      <Wrapper>
        <div className="row gy-5 g-xl-5">
          <div className="col-xxl-12">
            <div className="card card-xxl-stretch mb-5 mb-xl-8">
              <div className="card-header border-bottom-dashed">
                <h3 className="card-title fw-bolder">Profit and Loss</h3>
              </div>
              <div className="card-body py-2">
                {content}
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
  );
};

export default PL;

